import { useState } from 'react';

export default function BookingCTA() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (name === 'message') setCharCount(value.length);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => params.append(k, v));
      await fetch('https://readdy.ai/api/form/d6v42c6dp0k8rarn7ru0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* CTA Banner */}
      <section className="relative w-full overflow-hidden" style={{ height: '480px' }}>
        <img
          src="https://public.readdy.ai/ai/img_res/3caebca1de3e4adc1605e81780bace51.jpg"
          alt="ご予約"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-black/50" />
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center text-center px-8 gap-6">
          <p
            className="text-white/70 text-xs tracking-[0.4em]"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Complete Reservation
          </p>
          <h2
            className="text-white text-4xl md:text-5xl font-light leading-snug"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            特別なひとときを
            <br />
            <span className="italic">今すぐご予約</span>
          </h2>
          <p
            className="text-white/70 text-sm tracking-widest"
            style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
          >
            完全予約制・当日予約可能
          </p>
          <div className="flex gap-3 flex-wrap justify-center">
            <span
              className="text-white/60 text-xs border border-white/30 rounded-full px-4 py-1.5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              初回限定 20%OFF
            </span>
            <span
              className="text-white/60 text-xs border border-white/30 rounded-full px-4 py-1.5"
              style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
            >
              尼崎市 完全個室
            </span>
          </div>
        </div>
      </section>

      {/* Booking Form */}
      <section id="booking" className="py-24 px-8 bg-white">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-12">
            <p
              className="text-[#6b7c5c] text-xs tracking-[0.35em] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              Reservation &amp; Contact
            </p>
            <h2
              className="text-stone-800 text-3xl md:text-4xl font-light"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
            >
              ご予約・お問い合わせ
            </h2>
          </div>

          {submitted ? (
            <div className="text-center py-16 flex flex-col items-center gap-5">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-[#6b7c5c]/10">
                <i className="ri-check-line text-3xl text-[#6b7c5c]" />
              </div>
              <p
                className="text-stone-700 text-lg font-light"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                送信が完了しました
              </p>
              <p
                className="text-stone-400 text-sm leading-relaxed"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                お問い合わせありがとうございます。
                <br />
                2営業日以内にご連絡いたします。
              </p>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="flex flex-col gap-5"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label
                    className="text-stone-600 text-xs tracking-widest"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    お名前 <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="山田 花子"
                    className="text-sm border border-stone-200 rounded-lg px-4 py-3 outline-none focus:border-[#6b7c5c] transition-colors text-stone-700 placeholder:text-stone-300"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className="text-stone-600 text-xs tracking-widest"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    メールアドレス <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="example@email.com"
                    className="text-sm border border-stone-200 rounded-lg px-4 py-3 outline-none focus:border-[#6b7c5c] transition-colors text-stone-700 placeholder:text-stone-300"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-2">
                  <label
                    className="text-stone-600 text-xs tracking-widest"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    お電話番号
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="090-0000-0000"
                    className="text-sm border border-stone-200 rounded-lg px-4 py-3 outline-none focus:border-[#6b7c5c] transition-colors text-stone-700 placeholder:text-stone-300"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <label
                    className="text-stone-600 text-xs tracking-widest"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    ご希望の施術
                  </label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="text-sm border border-stone-200 rounded-lg px-4 py-3 outline-none focus:border-[#6b7c5c] transition-colors text-stone-700 bg-white cursor-pointer"
                    style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                  >
                    <option value="">選択してください</option>
                    <option value="ヘッドスパ・ドライヘッドマッサージ">ヘッドスパ・ドライヘッドマッサージ</option>
                    <option value="フェイシャル・小顔矯正">フェイシャル・小顔矯正</option>
                    <option value="よもぎ蒸し">よもぎ蒸し</option>
                    <option value="リンパマッサージ">リンパマッサージ</option>
                    <option value="未定・相談したい">未定・相談したい</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  className="text-stone-600 text-xs tracking-widest flex items-center justify-between"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                >
                  <span>メッセージ・ご要望</span>
                  <span className={`text-xs ${charCount > 500 ? 'text-red-400' : 'text-stone-300'}`}>
                    {charCount}/500
                  </span>
                </label>
                <textarea
                  name="message"
                  rows={5}
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  placeholder="ご希望の日時、お悩みなどをお気軽にお書きください。"
                  className="text-sm border border-stone-200 rounded-lg px-4 py-3 outline-none focus:border-[#6b7c5c] transition-colors text-stone-700 placeholder:text-stone-300 resize-none"
                  style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
                />
                {charCount > 500 && (
                  <p className="text-red-400 text-xs" style={{ fontFamily: "'Noto Sans JP', sans-serif" }}>
                    500文字以内でご入力ください
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting || charCount > 500}
                className="whitespace-nowrap mt-2 bg-[#6b7c5c] hover:bg-[#5a6a4c] disabled:bg-stone-300 text-white text-sm tracking-[0.3em] py-4 rounded-full transition-all duration-300 cursor-pointer flex items-center justify-center gap-2"
                style={{ fontFamily: "'Noto Sans JP', sans-serif" }}
              >
                {submitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    送信中...
                  </>
                ) : (
                  '送信する'
                )}
              </button>
            </form>
          )}
        </div>
      </section>
    </>
  );
}
